import { Ban, Clock, Copy, Flag, MessageSquare, ShieldOff, UserMinus, UserPlus, UserX, VolumeX } from 'lucide-react';
import { Permission } from '@spawnwire/shared';
import type { MenuEntry } from '../../components/ui';
import { http } from '../../lib/api';
import { attempt, openDm } from '../../lib/data';
import { getSocket } from '../../lib/gateway';
import { copyText } from '../../lib/hooks';
import { navigate } from '../../lib/nav';
import { can, canActOn, spacePermissions } from '../../lib/perms';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { appMenuEntries } from '../chat/slashCommands';

/** Context menu entries for a person, used in the member list, messages and voice tiles. */
export function userMenu(userId: string, spaceId: string | null, opts: { voiceChannelId?: string } = {}): MenuEntry[] {
  const state = useStore.getState();
  const me = state.me;
  if (!me) return [];
  const self = userId === me.id;
  // Bots can't be messaged or befriended.
  const bot = !!state.users[userId]?.bot;
  const rel = state.relationships[userId]?.type ?? null;
  const perms = spaceId ? spacePermissions(state, spaceId) : 0;
  const actOn = spaceId ? canActOn(state, spaceId, userId) : false;
  const voice = opts.voiceChannelId ? state.voiceStates[opts.voiceChannelId]?.[userId] : undefined;

  const moderate = (payload: object) =>
    getSocket()?.emit('voice:moderate', { channelId: opts.voiceChannelId!, userId, ...payload }, (res) => {
      if (!res.ok) ui.toast(res.error, 'error');
    });

  return [
    { label: 'Profile', icon: UserPlus, onSelect: () => ui.open({ kind: 'profile', userId, spaceId }) },
    {
      label: 'Message',
      icon: MessageSquare,
      hidden: self || bot,
      onSelect: () =>
        void openDm(userId).then((c) => {
          if (c) navigate(`/app/@me/${c.id}`);
        }),
    },
    { label: 'Add friend', icon: UserPlus, hidden: self || bot || rel === 'friend' || rel === 'outgoing' || rel === 'blocked', onSelect: () => void attempt(() => http.put(`/relationships/${userId}`, { type: 'friend' }), rel === 'incoming' ? 'Friend request accepted.' : 'Friend request sent.') },
    { label: 'Remove friend', icon: UserMinus, hidden: rel !== 'friend', onSelect: () => void attempt(() => http.del(`/relationships/${userId}`)) },
    {
      label: rel === 'blocked' ? 'Unblock' : 'Block',
      icon: ShieldOff,
      hidden: self,
      danger: rel !== 'blocked',
      onSelect: () =>
        rel === 'blocked'
          ? void attempt(() => http.del(`/relationships/${userId}`))
          : ui.confirm({
              title: 'Block this person?',
              body: 'They will not be able to message you or send friend requests, and their messages will be hidden. They are not notified.',
              confirmLabel: 'Block',
              danger: true,
              onConfirm: () => http.put(`/relationships/${userId}`, { type: 'blocked' }),
            }),
    },
    { label: voice?.serverMute ? 'Server unmute' : 'Server mute', icon: VolumeX, hidden: !voice || !spaceId || !can(perms, Permission.MUTE_MEMBERS) || (!actOn && !self), separatorBefore: true, onSelect: () => moderate({ serverMute: !voice?.serverMute }) },
    { label: 'Disconnect from voice', icon: UserX, hidden: !voice || !spaceId || !can(perms, Permission.MOVE_MEMBERS) || !actOn, onSelect: () => moderate({ disconnect: true }) },
    { label: 'Time out', icon: Clock, hidden: !spaceId || !actOn || !can(perms, Permission.MODERATE_MEMBERS), separatorBefore: !voice, onSelect: () => ui.open({ kind: 'moderate', action: 'timeout', spaceId: spaceId!, userId }) },
    { label: 'Kick', icon: UserMinus, danger: true, hidden: !spaceId || !actOn || !can(perms, Permission.KICK_MEMBERS), onSelect: () => ui.open({ kind: 'moderate', action: 'kick', spaceId: spaceId!, userId }) },
    { label: 'Ban', icon: Ban, danger: true, hidden: !spaceId || !actOn || !can(perms, Permission.BAN_MEMBERS), onSelect: () => ui.open({ kind: 'moderate', action: 'ban', spaceId: spaceId!, userId }) },
    { label: 'Report', icon: Flag, hidden: self, separatorBefore: true, onSelect: () => ui.open({ kind: 'report', userId, spaceId }) },
    { label: 'Copy user ID', icon: Copy, onSelect: () => void copyText(userId) },
    // Commands the bots in this space offer for a person ("Apps").
    ...appMenuEntries(spaceId, 'user', userId),
  ];
}
